import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  User, CreditCard, FileText, Receipt, TrendingUp, Sparkles, MessageSquare,
  LogOut, Upload, Download, Loader2, CheckCircle2, Clock, XCircle, Send, Eye, Share2, Copy,
} from "lucide-react";
import { InvoicesTab } from "./admin/InvoicesTab";
import { DocumentStudioTab } from "./admin/DocumentStudioTab";
import { MyTestimonialsTab } from "./admin/MyTestimonialsTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = process.env.REACT_APP_BACKEND_URL;

const authHeader = () => {
  const t = localStorage.getItem("admin_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const STATUS_COLOR = {
  paid: "text-green-700 bg-green-50 border-green-200",
  sent: "text-blue-700 bg-blue-50 border-blue-200",
  overdue: "text-red-700 bg-red-50 border-red-200",
  signed: "text-green-700 bg-green-50 border-green-200",
};

export default function ContractorPortal() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("profile");
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/team/me`, { headers: authHeader() });
      setProfile(data);
      // If somehow an admin/super-admin lands here, redirect
      if (data.role === "super_admin" || data.role === "admin") {
        navigate("/admin/dashboard", { replace: true });
      }
    } catch (e) {
      // Token expired or missing — show the portal login screen, don't bounce to /admin
      localStorage.removeItem("admin_token");
      localStorage.removeItem("admin_info");
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!localStorage.getItem("admin_token")) {
      setLoading(false);
      return;
    }
    load();
  }, [load]);

  const logout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_info");
    setProfile(null);
    setLoading(false);
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    if (file.size > 350 * 1024) {
      toast.error("Photo must be under 350KB. Please compress and retry.");
      return;
    }
    setUploading(true);
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        await axios.patch(`${API}/team/me`, { photo_base64: e.target.result }, { headers: authHeader() });
        toast.success("Photo updated");
        load();
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Upload failed");
      } finally {
        setUploading(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const saveField = async (key, value) => {
    try {
      await axios.patch(`${API}/team/me`, { [key]: value }, { headers: authHeader() });
      toast.success("Saved");
      load();
    } catch (e) {
      toast.error("Save failed");
    }
  };

  const downloadIdCard = async () => {
    try {
      const res = await fetch(`${API}/team/me/id-card`, { headers: authHeader() });
      if (!res.ok) throw new Error("Failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `MK_ID_Card_${profile?.member_id || "card"}.pdf`;
      a.click();
      toast.success("ID Card downloaded");
    } catch {
      toast.error("Download failed");
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-[#FAFAF8]"><Loader2 className="animate-spin text-[#B8860B]" /></div>;
  }

  if (!profile) {
    return <PortalLoginScreen onSuccess={() => { setLoading(true); load(); }} navigate={navigate} />;
  }

  const tabs = [
    { id: "profile",    label: "Profile",     icon: User },
    { id: "idcard",     label: "ID Card",     icon: CreditCard },
    { id: "documents",   label: "Documents",    icon: FileText },
    { id: "studio",      label: "Studio",       icon: Sparkles },
    { id: "testimonials",label: "Testimonials", icon: MessageSquare },
    { id: "referrals",  label: "My Referrals", icon: Share2 },
    { id: "invoices",    label: "Invoices",     icon: Receipt },
    { id: "earnings",   label: "Earnings",    icon: TrendingUp },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAF8] font-['Manrope']">
      {/* Header */}
      <header className="bg-[#1A1A1A] text-white border-b-2 border-[#B8860B]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold tracking-wide">MAGNUS KOR</h1>
            <p className="text-xs text-[#B8860B]">TALENT PARTNER PORTAL</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right hidden sm:block">
              <p className="text-sm font-medium">{profile.name}</p>
              <p className="text-xs text-gray-400">{profile.title} / {profile.member_id}</p>
            </div>
            <button onClick={logout} className="flex items-center gap-1.5 text-xs text-gray-300 hover:text-white" data-testid="logout-btn">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6">
        {/* Sidebar */}
        <aside className="bg-white border border-gray-200 rounded h-fit sticky top-6">
          <nav className="p-2">
            {tabs.map((t) => (
              <button key={t.id} onClick={() => setActiveTab(t.id)} data-testid={`portal-tab-${t.id}`}
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded transition-colors ${
                  activeTab === t.id ? "bg-[#B8860B] text-white" : "text-gray-700 hover:bg-gray-50"
                }`}>
                <t.icon size={16} strokeWidth={1.5} /> {t.label}
              </button>
            ))}
          </nav>
        </aside>

        {/* Main */}
        <main className="bg-white border border-gray-200 rounded min-h-[500px]">
          {activeTab === "profile" && (
            <div className="p-6" data-testid="portal-profile">
              <h2 className="text-xl font-medium mb-6">My Profile</h2>
              <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-6">
                <div>
                  <div className="w-40 h-48 bg-gray-100 rounded overflow-hidden border border-gray-200 relative">
                    {profile.photo_base64 ? (
                      <img src={profile.photo_base64} alt="Profile" className="w-full h-full object-cover" />
                    ) : (
                      <div className="flex items-center justify-center w-full h-full text-4xl text-[#B8860B] font-bold">
                        {profile.name.split(" ").map((p) => p[0]).join("").slice(0, 2)}
                      </div>
                    )}
                  </div>
                  <input type="file" accept="image/*" ref={fileInputRef} className="hidden"
                    onChange={(e) => uploadPhoto(e.target.files?.[0])} data-testid="photo-input" />
                  <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
                    className="mt-3 w-40 flex items-center justify-center gap-2 text-xs text-[#B8860B] border border-[#B8860B] px-3 py-2 hover:bg-[#FDF8ED] disabled:opacity-50"
                    data-testid="upload-photo-btn">
                    {uploading ? <Loader2 className="animate-spin" size={12} /> : <Upload size={12} />}
                    {profile.photo_base64 ? "Change Photo" : "Upload Photo"}
                  </button>
                </div>
                <div className="space-y-3">
                  <Field label="Full Name" value={profile.name} onSave={(v) => saveField("name", v)} testId="field-name" />
                  <Field label="Specialist" value={profile.specialist} onSave={(v) => saveField("specialist", v)} testId="field-specialist"
                    options={["Engineering","Product","Sales","Growth","Business Development","Operations","Customer Success","Recruiting","Finance","Marketing","Talent Advisory"]} />
                  <Info label="Title" value={profile.title} />
                  <Info label="Member ID" value={profile.member_id} />
                  <Info label="Username" value={profile.username} />
                  <Info label="Email" value={profile.email} />
                  <Info label="Password" value="•••••••• (contact admin to reset)" />
                  <Info label="Joined" value={profile.join_date} />
                  <Info label="Valid Until" value={profile.expiry_date || "Permanent"} />
                  {profile.role === "contractor" && (
                    <Info label="Commission Tier" value={(() => {
                    const t = profile.commission_tier;
                    const map = { "10": "BD Partner", "20": "Associate", "25": "Senior", "30": "Lead" };
                    return t ? `${t}% (${map[t] || "Custom"})` : "—";
                  })()} />
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === "idcard" && (
            <div className="p-6" data-testid="portal-idcard">
              <h2 className="text-xl font-medium mb-2">Magnus Kor ID Card</h2>
              <p className="text-sm text-gray-500 mb-6">Your official Magnus Kor identification. Download, print, and carry with you.</p>

              <div className="bg-gradient-to-br from-[#1A1A1A] to-[#2A2A2A] text-white rounded-lg overflow-hidden shadow-xl max-w-xl" data-testid="idcard-preview">
                <div className="border-l-4 border-[#B8860B] p-6">
                  <div className="flex items-start justify-between mb-6">
                    <div>
                      <h3 className="text-lg font-bold tracking-wide">MAGNUS KOR</h3>
                      <p className="text-xs text-[#B8860B] tracking-wider">TALENT PARTNERS / OFFICIAL ID</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-400">MEMBER ID</p>
                      <p className="text-sm font-bold">{profile.member_id}</p>
                    </div>
                  </div>
                  <div className="flex gap-5">
                    <div className="w-24 h-32 bg-gray-700 border border-[#B8860B] rounded overflow-hidden flex-shrink-0">
                      {profile.photo_base64 ? (
                        <img src={profile.photo_base64} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="flex items-center justify-center w-full h-full text-2xl text-[#B8860B] font-bold">
                          {profile.name.split(" ").map((p) => p[0]).join("").slice(0, 2)}
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="text-lg font-bold">{profile.name.toUpperCase()}</p>
                      <p className="text-xs text-[#B8860B] tracking-wider mb-2">{profile.title.toUpperCase()}</p>
                      {profile.specialist && <p className="text-xs text-gray-300 mb-3">SPECIALIST: {profile.specialist.toUpperCase()}</p>}
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div><span className="text-gray-400">JOINED</span><p className="font-bold">{formatDate(profile.join_date)}</p></div>
                        <div><span className="text-gray-400">VALID UNTIL</span><p className="font-bold">{profile.expiry_date ? formatDate(profile.expiry_date) : "Permanent"}</p></div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-[#B8860B] text-[#1A1A1A] px-6 py-2 text-xs font-bold flex justify-between">
                  <span>AUTHORISED REPRESENTATIVE</span>
                  <span>L&D NEXUS LTD / LONDON</span>
                </div>
              </div>

              <button onClick={downloadIdCard}
                className="mt-6 flex items-center gap-2 bg-[#B8860B] text-white px-6 py-3 hover:bg-[#9A7209]"
                data-testid="download-idcard-btn">
                <Download size={16} /> Download ID Card (PDF)
              </button>

              <div className="mt-8 text-xs text-gray-500 space-y-1 max-w-xl">
                <p>- This card contains a front (photo side) and back (terms & issuer).</p>
                <p>- Print at 100% scale on thick cardstock (CR80 size, 85.6 x 54mm).</p>
                <p>- Keep your photo current - re-upload in the Profile tab if needed.</p>
              </div>
            </div>
          )}

          {activeTab === "documents" && <ContractorDocumentsView />}
          {activeTab === "studio" && <DocumentStudioTab />}
          {activeTab === "testimonials" && <MyTestimonialsTab profile={profile} />}
          {activeTab === "referrals" && <MyReferralsView profile={profile} />}
          {activeTab === "invoices" && <InvoicesTab />}
          {activeTab === "earnings" && <EarningsView profile={profile} />}
        </main>
      </div>
    </div>
  );
}

// ============ Sub-components ============

function Field({ label, value, onSave, options, testId }) {
  const [v, setV] = useState(value || "");
  const [editing, setEditing] = useState(false);
  const save = () => { onSave(v); setEditing(false); };
  return (
    <div className="flex items-center gap-3">
      <label className="text-xs text-gray-500 uppercase tracking-wider w-32">{label}</label>
      {editing ? (
        options ? (
          <select value={v} onChange={(e) => setV(e.target.value)}
            className="flex-1 border border-gray-200 px-3 py-1.5 text-sm focus:outline-none focus:border-[#B8860B]" data-testid={testId}>
            <option value="">-- Select --</option>
            {options.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        ) : (
          <input value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => e.key === "Enter" && save()}
            className="flex-1 border border-gray-200 px-3 py-1.5 text-sm focus:outline-none focus:border-[#B8860B]" data-testid={testId} />
        )
      ) : (
        <p className="flex-1 text-sm text-gray-800" data-testid={`${testId}-display`}>{value || <span className="text-gray-400">Not set</span>}</p>
      )}
      {editing ? (
        <><button onClick={save} className="text-xs text-green-600 hover:underline">Save</button>
        <button onClick={() => { setV(value); setEditing(false); }} className="text-xs text-gray-400 hover:underline">Cancel</button></>
      ) : (
        <button onClick={() => setEditing(true)} className="text-xs text-[#B8860B] hover:underline">Edit</button>
      )}
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="flex items-center gap-3">
      <label className="text-xs text-gray-500 uppercase tracking-wider w-32">{label}</label>
      <p className="flex-1 text-sm text-gray-700">{value || <span className="text-gray-400">-</span>}</p>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "-";
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
}

function ContractorDocumentsView() {
  // Curated subset visible to contractors. Static contracts (MSA, Candidate
  // Consent) are generated via Studio; founder-only docs (Partnerships,
  // Op Framework) and admin-only invoices/agreements are intentionally excluded.
  const docs = [
    // Sales & branding collateral
    { title: "Company Brochure",                 endpoint: "/api/documents/brochure",                  format: "PDF" },
    { title: "Brand Kit",                        endpoint: "/api/documents/brand-kit",                 format: "PDF" },
    { title: "Letterhead Template",              endpoint: "/api/documents/letterhead",                format: "PDF" },
    { title: "Email Signature Banner",           endpoint: "/api/documents/email-signature",           format: "PNG" },
    { title: "Gold Monogram (personal branding)",endpoint: "/api/documents/monogram-gold",             format: "PNG" },

    // Training & playbooks
    { title: "Recruiter Playbook",               endpoint: "/api/documents/recruiter-playbook",        format: "PDF" },
    { title: "BD Playbook",                      endpoint: "/api/documents/bd-playbook",               format: "PDF" },
    { title: "Quick Start Card",                 endpoint: "/api/documents/quick-start-card",          format: "PDF" },
    { title: "Onboarding Deck",                  endpoint: "/api/documents/onboarding-deck",           format: "PDF" },

    // Productivity spreadsheets
    { title: "Pipeline Tracker",                 endpoint: "/api/documents/pipeline-tracker",          format: "XLSX" },
    { title: "Team Earnings Calculator",         endpoint: "/api/documents/team-earnings-calculator",  format: "XLSX" },
    { title: "BD Pipeline Tracker",              endpoint: "/api/documents/bd-pipeline-tracker",       format: "XLSX" },

    // Candidate-facing assets
    { title: "ATS CV Template",                  endpoint: "/api/documents/ats-cv-template",           format: "DOCX" },
    { title: "Cover Letter Template",            endpoint: "/api/documents/cover-letter-template",     format: "DOCX" },
  ];
  return (
    <div className="p-6" data-testid="portal-documents">
      <h2 className="text-xl font-medium mb-2">Documents</h2>
      <p className="text-sm text-gray-500 mb-6">Reference library: training, playbooks, brand assets and productivity templates. To generate personalised client/candidate contracts, use the <span className="text-[#B8860B] font-medium">Studio</span> tab.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {docs.map((d, i) => (
          <a key={i} href={`${BASE_URL}${d.endpoint}`} target="_blank" rel="noopener noreferrer"
            className="group flex items-center gap-3 p-4 border border-gray-100 hover:border-[#B8860B] rounded transition-colors"
            data-testid={`doc-${i}`}>
            <FileText size={18} className="text-[#B8860B]" strokeWidth={1.5} />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-800">{d.title}</p>
              <p className="text-xs text-gray-400">{d.format}</p>
            </div>
            <Download size={14} className="text-gray-300 group-hover:text-[#B8860B]" />
          </a>
        ))}
      </div>
    </div>
  );
}

function EarningsView({ profile }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/invoices`, { headers: authHeader() })
      .then(({ data }) => setInvoices(Array.isArray(data) ? data : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const earnings = profile.earnings || { total_earnings: 0, pending_amount: 0, total_paid_invoices: 0, pending_invoices: 0 };

  return (
    <div className="p-6" data-testid="portal-earnings">
      <h2 className="text-xl font-medium mb-6">Earnings</h2>
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-8">
        <Stat label="Total Earned" value={`£${earnings.total_earnings.toLocaleString()}`} colour="text-green-600" />
        <Stat label="Pending" value={`£${earnings.pending_amount.toLocaleString()}`} colour="text-amber-600" />
        <Stat label="Paid Invoices" value={earnings.total_paid_invoices} />
        <Stat label="Pending Invoices" value={earnings.pending_invoices} />
      </div>

      <h3 className="text-sm font-medium mb-3 text-gray-600 uppercase tracking-wider">Your Invoices</h3>
      {loading ? (
        <div className="text-center py-8 text-gray-400"><Loader2 className="animate-spin inline" size={14} /></div>
      ) : invoices.length === 0 ? (
        <p className="text-sm text-gray-400">No invoices yet. Create one from the Invoices tab.</p>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              <th className="text-left p-3">#</th>
              <th className="text-left p-3">Client</th>
              <th className="text-right p-3">Amount</th>
              <th className="text-right p-3">Your Commission</th>
              <th className="text-left p-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((i) => (
              <tr key={i.id} className="border-b border-gray-50">
                <td className="p-3">{i.invoice_number}</td>
                <td className="p-3">{i.client_name}</td>
                <td className="p-3 text-right">{i.currency} {i.total_amount?.toLocaleString()}</td>
                <td className="p-3 text-right text-[#B8860B] font-medium">{i.currency} {(i.contractor_commission || 0).toLocaleString()}</td>
                <td className="p-3">
                  <span className={`px-2 py-0.5 text-xs border rounded-full ${STATUS_COLOR[i.status] || "bg-gray-50 text-gray-600 border-gray-200"}`}>
                    {i.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function Stat({ label, value, colour = "text-gray-800" }) {
  return (
    <div className="border border-gray-100 p-4 rounded">
      <p className="text-xs uppercase text-gray-500 tracking-wider">{label}</p>
      <p className={`text-2xl font-bold mt-1 ${colour}`}>{value}</p>
    </div>
  );
}


function MyReferralsView({ profile }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const link = `${origin}/for-startups?ref=${profile.id}`;
  const qrSrc = `${API}/referral/qr?ref=${profile.id}&base=${encodeURIComponent(origin)}`;

  useEffect(() => {
    axios.get(`${API}/team/my-referrals`, { headers: authHeader() })
      .then((res) => setItems(res.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const copy = () => {
    navigator.clipboard.writeText(link);
    setCopied(true);
    toast.success("Referral link copied");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="p-6" data-testid="portal-referrals">
      <h2 className="text-xl font-medium mb-1">My Referrals</h2>
      <p className="text-sm text-gray-500 mb-6">Share your link with startups. Any hiring inquiry submitted through it is credited to you and appears below.</p>

      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-6 bg-[#FAFAF8] border border-gray-200 rounded p-5 mb-8">
        <div>
          <label className="text-xs text-gray-500 block mb-2">Your unique referral link</label>
          <div className="flex items-stretch gap-2">
            <input readOnly value={link} className="flex-grow border border-gray-300 rounded px-3 py-2 text-sm bg-white" data-testid="referral-link" onFocus={(e) => e.target.select()} />
            <button onClick={copy} className="flex items-center gap-1.5 px-3 py-2 bg-[#B8860B] text-white text-sm rounded hover:bg-[#a67a0a]" data-testid="referral-copy-btn">
              {copied ? <CheckCircle2 size={15} /> : <Copy size={15} />} {copied ? "Copied" : "Copy"}
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-3">Tip: add this link to your email signature and LinkedIn, or share the QR code at events.</p>
        </div>
        <div className="flex flex-col items-center justify-center">
          <img src={qrSrc} alt="Referral QR code" className="w-32 h-32 border border-gray-200 rounded bg-white p-1" data-testid="referral-qr" />
          <a href={qrSrc} download={`magnuskor-referral-${profile.member_id || "qr"}.png`} className="text-xs text-[#B8860B] mt-2 flex items-center gap-1"><Download size={12} /> Download QR</a>
        </div>
      </div>

      <h3 className="text-sm font-semibold text-gray-700 mb-3">Startups referred by you {items.length > 0 && <span className="text-gray-400 font-normal">({items.length})</span>}</h3>
      {loading ? (
        <div className="py-10 text-center text-gray-400"><Loader2 className="animate-spin inline" size={18} /></div>
      ) : items.length === 0 ? (
        <div className="py-10 text-center text-gray-400 border border-dashed border-gray-200 rounded" data-testid="referrals-empty">No referrals yet. Share your link above to get started.</div>
      ) : (
        <div className="space-y-3">
          {items.map((inq) => (
            <div key={inq.id} className="border border-gray-200 rounded p-4" data-testid="referral-item">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-medium text-gray-900">{inq.company_name}</p>
                  <p className="text-sm text-gray-500">Looking for: {inq.role_title} · {inq.role_department} · {inq.role_seniority}</p>
                  <p className="text-xs text-gray-400 mt-1">{inq.contact_name} · {inq.email}{inq.phone ? ` · ${inq.phone}` : ""}</p>
                </div>
                <span className="text-xs px-2 py-0.5 rounded bg-[#B8860B]/10 text-[#B8860B] whitespace-nowrap capitalize">{(inq.status || "new").replace(/_/g, " ")}</span>
              </div>
              {inq.funding_stage && <p className="text-xs text-gray-400 mt-2">Stage: {inq.funding_stage}{inq.urgency ? ` · Timeline: ${inq.urgency}` : ""}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PortalLoginScreen({ onSuccess, navigate }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      toast.error("Please enter your username and password");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { username, password });
      localStorage.setItem("admin_token", data.access_token);
      localStorage.setItem("admin_info", JSON.stringify(data.admin || {}));
      const role = data.admin?.role;
      if (role === "super_admin" || role === "admin") {
        // Admin entered via the wrong door — quietly route to admin dashboard
        toast.success("Signed in. Redirecting to admin dashboard…");
        navigate("/admin/dashboard", { replace: true });
        return;
      }
      toast.success(`Welcome back, ${data.admin?.name || "Talent Partner"}!`);
      onSuccess();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid username or password");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-[#0f0f10] via-[#15151a] to-[#0f0f10] text-white" data-testid="portal-login-screen">
      {/* Brand bar */}
      <header className="border-b border-white/5 px-6 py-5">
        <div className="max-w-5xl mx-auto">
          <p className="text-[#B8860B] font-serif text-xl tracking-wide">MAGNUS KOR</p>
          <p className="text-[10px] text-gray-400 tracking-[0.25em] uppercase">Talent Partner Portal</p>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-6">
        <div className="w-full max-w-md">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#B8860B]/15 border border-[#B8860B]/30 text-[#D4A12C] text-[10px] uppercase tracking-[0.18em] rounded-full mb-4">
              <Sparkles size={11} /> Talent Partner Access
            </div>
            <h1 className="text-3xl font-serif text-white mb-2">Welcome back</h1>
            <p className="text-sm text-gray-400">Sign in to access your portal, studio and earnings.</p>
          </div>

          <form onSubmit={handleSubmit} className="bg-[#1a1a1f] border border-white/10 rounded-xl p-7 space-y-4">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.18em] text-gray-400 mb-1.5">Username</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="your username"
                autoComplete="username"
                className="w-full bg-[#0f0f10] border border-white/10 focus:border-[#B8860B] outline-none px-3 py-2.5 text-sm rounded text-white placeholder-gray-600"
                data-testid="portal-login-username"
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.18em] text-gray-400 mb-1.5">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
                className="w-full bg-[#0f0f10] border border-white/10 focus:border-[#B8860B] outline-none px-3 py-2.5 text-sm rounded text-white placeholder-gray-600"
                data-testid="portal-login-password"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-[#B8860B] hover:bg-[#9A7209] disabled:opacity-50 text-white font-medium text-sm py-3 rounded transition-colors"
              data-testid="portal-login-submit"
            >
              {submitting ? "Signing in…" : "Sign in to Portal"}
            </button>
            <p className="text-[11px] text-gray-500 text-center pt-2">
              Forgot your password? Contact your admin to reset it.
            </p>
          </form>

          <div className="mt-8 text-center text-xs text-gray-500">
            <span>Are you an admin? </span>
            <button
              onClick={() => navigate("/admin", { replace: true })}
              className="text-[#B8860B] hover:underline"
              data-testid="portal-login-admin-switch"
            >
              Sign in to admin dashboard →
            </button>
          </div>
        </div>
      </main>

      <footer className="text-center py-6 text-[11px] text-gray-500">
        L&D Nexus Limited · Company No. 16450617 · London WC2H 9JQ
      </footer>
    </div>
  );
}